import type { AxiosProgressEvent } from "axios";
import { get } from "lodash-es";
import { axiosInstance } from "../../../../context/axiosInstance";
import {
	MESApiService,
	type spParameter,
} from "../../../../services/ApiService";
import { getError } from "../../../../utils";
import type { AppRelease } from "../models/AppRelease";

export const getAppReleases = async (
	{
		appCode = null,
		platform = null,
		appReleaseId = null,
	}: {
		appCode?: string | null;
		platform?: string | null;
		appReleaseId?: number | null;
	} = {},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AppCode", value: appCode },
		{ name: "Platform", value: platform },
		{ name: "AppReleaseId", value: appReleaseId },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[GetAppReleases]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as AppRelease[];
	}

	throw new Error(resp.message || "Error fetching app releases");
};

export type UpdateAppReleaseProps = {
	AppReleaseId: number;
	AppCode: string;
	Platform: string;
	Version: string;
	Build: number;
	MinSupportedVersion: string | null;
	ReleaseNotes: string | null;
	IsActive: boolean;
};

/** Edits the metadata of an existing release. The file cannot be changed. */
export const updateAppRelease = async (release: UpdateAppReleaseProps) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AppReleaseId", value: release.AppReleaseId },
		{ name: "AppCode", value: release.AppCode },
		{ name: "Platform", value: release.Platform },
		{ name: "Version", value: release.Version },
		{ name: "Build", value: release.Build },
		{ name: "MinSupportedVersion", value: release.MinSupportedVersion },
		{ name: "ReleaseNotes", value: release.ReleaseNotes },
		{ name: "IsActive", value: release.IsActive },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[UpsertAppRelease]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving the app release");
	}
};

export const setAppReleaseActive = async ({
	appReleaseId,
	isActive,
}: {
	appReleaseId: number;
	isActive: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AppReleaseId", value: appReleaseId },
		{ name: "IsActive", value: isActive },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[SetAppReleaseActive]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message ||
				`Error when ${isActive ? "activating" : "deactivating"} the release`,
		);
	}
};

export const deleteAppRelease = async (appReleaseId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AppReleaseId", value: appReleaseId },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[DeleteAppRelease]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the app release");
	}
};

export type UploadAppReleaseProps = {
	file: File;
	appCode: string;
	platform: string;
	version: string;
	build: number;
	minSupportedVersion: string | null;
	releaseNotes: string | null;
	isActive: boolean;
	/** Receives the upload progress, from 0 to 100. */
	onProgress?: (percent: number) => void;
};

/** Uploads the APK and creates the release in a single multipart request. */
export const uploadAppRelease = async ({
	file,
	appCode,
	platform,
	version,
	build,
	minSupportedVersion,
	releaseNotes,
	isActive,
	onProgress,
}: UploadAppReleaseProps) => {
	const formData = new FormData();
	formData.append("file", file, file.name);
	formData.append("appCode", appCode);
	formData.append("platform", platform);
	formData.append("version", version);
	formData.append("build", String(build));
	if (minSupportedVersion) {
		formData.append("minSupportedVersion", minSupportedVersion);
	}
	if (releaseNotes) {
		formData.append("releaseNotes", releaseNotes);
	}
	formData.append("isActive", String(isActive));

	try {
		const response = await axiosInstance.post("/appReleases", formData, {
			timeout: 0,
			onUploadProgress: (event: AxiosProgressEvent) => {
				if (onProgress && event.total) {
					onProgress(Math.round((event.loaded * 100) / event.total));
				}
			},
		});
		return response.data;
	} catch (err) {
		throw new Error(getError(err) || "Error uploading the app release");
	}
};

const getFileNameFromContentDisposition = (header?: string | null) => {
	if (!header) {
		return null;
	}

	const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
	if (utf8Match) {
		return decodeURIComponent(utf8Match[1].trim().replace(/"/g, ""));
	}

	const match = /filename="?([^";]+)"?/i.exec(header);
	return match ? match[1].trim() : null;
};

/** Downloads the APK through the authenticated endpoint and saves it. */
export const downloadAppRelease = async (release: AppRelease) => {
	try {
		const response = await axiosInstance.get(
			`/appReleases/${release.AppReleaseId}/download`,
			{ responseType: "blob", timeout: 0 },
		);

		const fileName =
			getFileNameFromContentDisposition(
				response.headers["content-disposition"] as string | undefined,
			) ?? `${release.AppCode}_${release.Version}-${release.Build}.apk`;

		const url = window.URL.createObjectURL(response.data as Blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	} catch (err) {
		// With responseType "blob" the error body arrives as a Blob too.
		const data: unknown = get(err, "response.data");
		if (data instanceof Blob) {
			let message: string | undefined;
			try {
				message = get(JSON.parse(await data.text()), "message");
			} catch {
				message = undefined;
			}
			if (message) {
				throw new Error(message);
			}
		}
		throw new Error(getError(err) || "Error downloading the app release");
	}
};
